<!-- src/components/BookmarkButton.vue -->
<template>
  <div class="bookmark-container">
    <div v-if="!isLoading && isBookmarked" class="bookmark-active">
      <NButton 
        type="error"
        @click="toggleBookmarkStatus"
        :loading="isLoading"
      >
        <template #icon>
          <span>❤️</span>
        </template>
        Remove from Bookmarks
      </NButton>
    </div>
    
    <div v-else-if="!isLoading && !isBookmarked" class="bookmark-inactive">
      <NButton 
        type="primary"
        @click="toggleBookmarkStatus"
        :loading="isLoading"
      >
        <template #icon>
          <span>🤍</span>
        </template>
        Add to Bookmarks
      </NButton>
    </div>
    
    <div v-else class="bookmark-loading">
      <NButton 
        :loading="true"
        disabled
      >
        <span class="loading-dots">
          Loading<span>.</span><span>.</span><span>.</span>
        </span>
      </NButton>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { NButton } from 'naive-ui';
import { toggleBookmark, isProviderBookmarked } from '../services/bookmark';
import { authToken, isAuthenticated } from '../services/auth';

const props = defineProps({
  providerId: {
    type: String,
    required: true
  },
  initialStatus: {
    type: Boolean,
    default: null
  }
});

const emit = defineEmits(['statusChange']);

const isLoading = ref(props.initialStatus === null);
const isBookmarked = ref(props.initialStatus);

onMounted(async () => {
  if (props.initialStatus === null && isAuthenticated.value) {
    await checkBookmarkStatus();
  }
});

const checkBookmarkStatus = async () => {
  try {
    isLoading.value = true;
    isBookmarked.value = await isProviderBookmarked(authToken.value, props.providerId);
  } catch (error) {
    console.error('Error checking bookmark status:', error);
    isBookmarked.value = false;
  } finally {
    isLoading.value = false;
  }
};

const toggleBookmarkStatus = async () => {
  try {
    isLoading.value = true;
    const newStatus = await toggleBookmark(
      authToken.value, 
      props.providerId, 
      isBookmarked.value
    );
    isBookmarked.value = newStatus;
    emit('statusChange', newStatus);
  } catch (error) {
    console.error('Error toggling bookmark:', error);
    // You could add error handling here, like showing a notification
  } finally {
    isLoading.value = false;
  }
};
</script>

<style scoped>
.bookmark-container {
  display: inline-block;
}

/* Optional loading animation */
.loading-dots span {
  animation: dots 1.5s infinite;
  animation-fill-mode: both;
}

.loading-dots span:nth-child(2) {
  animation-delay: 0.5s;
}

.loading-dots span:nth-child(3) {
  animation-delay: 1s;
}

@keyframes dots {
  0%, 80%, 100% { opacity: 0; }
  40% { opacity: 1; }
}
</style>